import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CompleteRiskAssessmentPayload } from 'api/RiskAssessmentsApi';
import { RiskAssessmentsApi } from 'api/RiskAssessmentsApi';
import { useFiRiskAssessmentCache } from 'tanstack-hooks/fi-risk-assessments/risk-assessments-cache';
import { useFiTaskCache } from 'tanstack-hooks/fi-tasks';
import { FI_TASK_KEY, RISK_ASSESSMENTS_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useCompleteRiskAssessment() {
  const riskAssessmentsApi = new RiskAssessmentsApi();
  const queryClient = useQueryClient();
  const { userBankId } = useUser();
  const { invalidateRiskAssessments } = useFiRiskAssessmentCache();
  const { invalidateTaskLists } = useFiTaskCache();

  const { mutateAsync: completeRiskAssessment, isPending: isCompletingRiskAssessment } = useMutation({
    mutationKey: [RISK_ASSESSMENTS_KEY, 'complete'],
    mutationFn: ({ id, ...data }: CompleteRiskAssessmentPayload & { id: string }) =>
      riskAssessmentsApi.completeRiskAssessment(userBankId, id, data),
    onSuccess: () => {
      invalidateRiskAssessments();
      // The backend completes the assessment's task, and the drawers only know it by task id.
      invalidateTaskLists();
      queryClient.invalidateQueries({ queryKey: [FI_TASK_KEY, userBankId] });
    },
    meta: {
      snackbarSuccessMessage: 'Risk assessment completed.',
      snackbarErrorMessage: 'There was an issue completing the risk assessment. Please contact support.'
    }
  });

  return { completeRiskAssessment, isCompletingRiskAssessment };
}
